import {
  JobPostCustomSkill,
  JobPostEscoSkill,
  JobPostRequiredSkill
} from 'core/interface/escoSkill';
import { IAutocompleteItem } from 'core/interface/autocompleteItem';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isStringRecord = (value: unknown): value is Record<string, string> =>
  isRecord(value) &&
  Object.values(value).every((entry) => typeof entry === 'string');

const coerceNameRecord = (
  name: unknown,
  title?: string
): Record<string, string> => {
  if (isStringRecord(name)) {
    return name;
  }
  if (typeof name === 'string' && name) {
    return { en: name };
  }
  if (title) {
    return { en: title };
  }
  return {};
};

export const normalizeEscoUri = (uri: string): string =>
  uri.trim().replace(/\/+$/, '').toLowerCase();

const extractUri = (record: Record<string, unknown>): string => {
  const candidates = [
    record.uri,
    record.URI,
    record.esco_uri,
    record.concept_uri,
    record.url
  ];
  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim()) {
      return candidate.trim();
    }
  }
  return '';
};

export const isEscoSkill = (
  skill: JobPostRequiredSkill
): skill is JobPostEscoSkill => 'uri' in skill && Boolean(skill.uri);

export const jobPostSkillIdentity = (skill: JobPostRequiredSkill): string => {
  if (isEscoSkill(skill)) {
    return `esco:${normalizeEscoUri(skill.uri)}`;
  }
  return `custom:${skill.name.trim().toLowerCase()}`;
};

export const jobPostSkillDisplayLabel = (
  skill: JobPostRequiredSkill,
  lang: string
): string => {
  if (isEscoSkill(skill)) {
    return (
      skill.title ||
      skill.name?.[lang] ||
      skill.name?.en ||
      Object.values(skill.name || {})[0] ||
      ''
    );
  }
  return skill.name;
};

/** Stable keys used to detect already-selected skills (URI and/or title/label). */
export const jobPostSkillMatchKeys = (
  skill: JobPostRequiredSkill,
  lang = 'en'
): string[] => {
  const keys = new Set<string>();
  keys.add(jobPostSkillIdentity(skill));

  if (isEscoSkill(skill)) {
    const title = skill.title?.trim().toLowerCase();
    if (title) keys.add(`title:${title}`);

    Object.values(skill.name || {}).forEach((value) => {
      const normalized = value?.trim().toLowerCase();
      if (normalized) keys.add(`title:${normalized}`);
    });
  } else {
    const name = skill.name?.trim().toLowerCase();
    if (name) keys.add(`title:${name}`);
  }

  const label = jobPostSkillDisplayLabel(skill, lang).trim().toLowerCase();
  if (label) keys.add(`title:${label}`);

  return Array.from(keys);
};

export const escoSearchResultMatchKeys = (result: {
  title: string;
  name?: Record<string, string>;
  uri: string;
}): string[] => {
  const keys = new Set<string>([`esco:${normalizeEscoUri(result.uri)}`]);
  const title = result.title?.trim().toLowerCase();
  if (title) keys.add(`title:${title}`);

  Object.values(result.name || {}).forEach((value) => {
    const normalized = value?.trim().toLowerCase();
    if (normalized) keys.add(`title:${normalized}`);
  });

  return Array.from(keys);
};

export const isEscoSearchResultSelected = (
  result: { title: string; name?: Record<string, string>; uri: string },
  selectedKeys: Set<string>
): boolean =>
  escoSearchResultMatchKeys(result).some((key) => selectedKeys.has(key));

export const isJobPostSkillSelected = (
  skill: JobPostRequiredSkill,
  selectedKeys: Set<string>,
  lang = 'en'
): boolean =>
  jobPostSkillMatchKeys(skill, lang).some((key) => selectedKeys.has(key));

export const normalizeJobPostSkill = (
  item: unknown
): JobPostRequiredSkill | null => {
  if (!item) return null;

  if (typeof item === 'string') {
    const trimmed = item.trim();
    return trimmed ? { name: trimmed } : null;
  }

  if (!isRecord(item)) return null;

  // Nested wrappers sometimes returned by APIs
  if (isRecord(item.skill)) {
    return normalizeJobPostSkill(item.skill);
  }
  if (isRecord(item.esco_skill)) {
    return normalizeJobPostSkill(item.esco_skill);
  }

  const uri = extractUri(item);
  const title =
    typeof item.title === 'string'
      ? item.title
      : typeof item.name === 'string'
        ? item.name
        : '';

  if (uri) {
    return {
      title,
      name: coerceNameRecord(item.name, title),
      uri
    };
  }

  if (typeof item.name === 'string' && item.name.trim()) {
    return { name: item.name.trim() };
  }

  if (typeof item.title === 'string' && item.title.trim()) {
    return { name: item.title.trim() };
  }

  // Multilingual name map without URI → treat preferred/en/first value as custom
  if (isStringRecord(item.name)) {
    const preferred =
      item.name.en || Object.values(item.name).find((value) => value.trim());
    if (preferred?.trim()) {
      return { name: preferred.trim() };
    }
  }

  return null;
};

const mapLegacySkillIdToCustomSkill = (
  item: unknown,
  legacySkillLookup?: IAutocompleteItem[] | null
): JobPostRequiredSkill | null => {
  if (typeof item !== 'number') return null;
  if (!legacySkillLookup?.length) return null;

  const matched = legacySkillLookup.find((skill) => skill.id === item);
  if (!matched?.name?.trim()) return null;

  return { name: matched.name.trim() };
};

export const normalizeJobPostSkillsFromApi = (
  data: unknown,
  legacySkillLookup?: IAutocompleteItem[] | null
): JobPostRequiredSkill[] => {
  const source = isRecord(data)
    ? (data as { Skills?: unknown; skills?: unknown; esco_skills?: unknown })
    : null;

  const raw =
    source?.Skills ?? source?.skills ?? source?.esco_skills ?? data;
  if (!Array.isArray(raw)) return [];

  const seen = new Set<string>();
  const normalized: JobPostRequiredSkill[] = [];

  raw.forEach((item) => {
    const skill =
      mapLegacySkillIdToCustomSkill(item, legacySkillLookup) ||
      normalizeJobPostSkill(item);
    if (!skill) return;
    const identity = jobPostSkillIdentity(skill);
    if (seen.has(identity)) return;
    seen.add(identity);
    normalized.push(skill);
  });

  return normalized;
};

export const jobPostSkillsToApiPayload = (
  skills: JobPostRequiredSkill[]
): Array<JobPostEscoSkill | JobPostCustomSkill> =>
  skills.map((skill) => {
    if (isEscoSkill(skill)) {
      return {
        title: skill.title,
        name: skill.name,
        uri: skill.uri
      };
    }
    return { name: skill.name };
  });

export const getJobSkillDisplayLabels = (
  skills: unknown,
  lang: string,
  legacySkillLookup?: Array<{ id: number; name: string }>
): string[] => {
  if (!Array.isArray(skills) || skills.length === 0) return [];

  return skills
    .map((skill) => {
      if (typeof skill === 'number' && legacySkillLookup?.length) {
        const match = legacySkillLookup.find((item) => item.id === skill);
        return match?.name || '';
      }

      const normalized = normalizeJobPostSkill(skill);
      return normalized ? jobPostSkillDisplayLabel(normalized, lang) : '';
    })
    .filter(Boolean);
};
